import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getCurrentUserDisplayName } from '../../auth/utils/user';
import { useAuth } from '../../auth/context/AuthContext';
import { useThemeContext } from '../../context/ThemeContext';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { toggleLanguage } from '../../store/slices/langSlice';
import styles from './AppBar.module.css';
import mercedesBenzLogo from '../../assets/images/Mercedes-Benz.svg';
import mopscLogo from '../../assets/images/MopscNew.png';
import ecptLogo from '../../assets/images/image (4).png';


const UserIcon: React.FC = () => (
  <svg viewBox="0 0 24 24">
    <path d="M20 21a8 8 0 0 0-16 0" />
    <circle cx="12" cy="8" r="4" />
  </svg>
);

const GlobeIcon: React.FC = () => (
  <svg viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18" />
    <path d="M12 3a14.5 14.5 0 0 1 0 18" />
    <path d="M12 3a14.5 14.5 0 0 0 0 18" />
  </svg>
);

const SunIcon: React.FC = () => (
  <svg viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2" />
    <path d="M12 20v2" />
    <path d="m4.93 4.93 1.41 1.41" />
    <path d="m17.66 17.66 1.41 1.41" />
    <path d="M2 12h2" />
    <path d="M20 12h2" />
    <path d="m6.34 17.66-1.41 1.41" />
    <path d="m19.07 4.93-1.41 1.41" />
  </svg>
);

const MoonIcon: React.FC = () => (
  <svg viewBox="0 0 24 24">
    <path d="M21 12.79A9 9 0 1 1 11.21 3c-.08.56-.12 1.13-.12 1.71A7.5 7.5 0 0 0 18.6 12.2c.58 0 1.15-.04 1.71-.12.24-.03.47.11.59.32.11.21.1.47-.03.66a8.96 8.96 0 0 1-.87 1.73z" />
  </svg>
);




const AppBar: React.FC = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const currentLang = useAppSelector((s) => s.lang.language);
  const { isDark, toggleTheme } = useThemeContext();

  const navigate = useNavigate();
  const { logout: ssoLogout } = useAuth();
  const isEcpt     = pathname.includes('/ecpt/');
  const isDfc      = pathname.includes('/dfc/');
  const isDigiTube = pathname.includes('/digi-tube/');
  const isOdin     = pathname.includes('/odin/');

  // Derive the version base path from the current URL
  const basePath = pathname.startsWith('/mopsc-app-station-v3')
    ? '/mopsc-app-station-v3'
    : pathname.startsWith('/mopsc-app-station-v2')
    ? '/mopsc-app-station-v2'
    : '/mopsc-app-station';

  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);

  const userName = getCurrentUserDisplayName();

  // Close dropdown on outside click
  useEffect(() => {
    const handlePointerDown = (e: PointerEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, []);

  const toggleLanguageHandler = () => {
    dispatch(toggleLanguage());
    // i18n stays in sync via LanguageSync in App.tsx
  };

  const currentLangLabel = currentLang === 'de' ? 'DE' : 'EN';

  const handleLogout = () => {
    setProfileOpen(false);
    ssoLogout();
  };

  const openProfileSettings = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setProfileOpen(false);
    navigate(`${basePath}/profile-settings`);
  };

  return (
    <header className={styles.header} data-ecpt={isEcpt || undefined} data-dfc={isDfc || undefined} data-digi-tube={isDigiTube || undefined} data-odin={isOdin || undefined}>
      <div className={styles.container}>
        <div className={styles.headerInner}>
          {/* Logo */}
          <div className={styles.logo}>
            {(isEcpt || isDfc || isDigiTube || isOdin)
              ? <span
                  className={styles.logoTextEcpt}
                  onClick={() => navigate(`${basePath}/home`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && navigate(`${basePath}/home`)}
                  style={{ cursor: 'pointer', fontStyle: 'normal' }}
                >MO/PSC</span>
              : <img src={mopscLogo} alt="MO/PSC" className={styles.logoImg} />
            }
            {!isEcpt && !isDfc && !isDigiTube && !isOdin && <span className={styles.logoText}>{t('homePage.header.logoText')}</span>}
            {isEcpt && (
              <>
                <span className={styles.logoDivider}>/</span>
                <img
                  src={ecptLogo}
                  alt="ECPT"
                  className={styles.logoImgEcpt}
                  onClick={() => navigate(`${basePath}/ecpt/home`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && navigate(`${basePath}/ecpt/home`)}
                  style={{ cursor: 'pointer' }}
                />
              </>
            )}
            {isDfc && (
              <>
                <span className={styles.logoDivider}>/</span>
                <span
                  className={styles.logoTextEcpt}
                  onClick={() => navigate(`${basePath}/dfc/home`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && navigate(`${basePath}/dfc/home`)}
                  style={{ cursor: 'pointer', fontStyle: 'normal' }}
                >DFC</span>
              </>
            )}
            {isDigiTube && (
              <>
                <span className={styles.logoDivider}>/</span>
                <span
                  className={styles.logoTextEcpt}
                  onClick={() => navigate(`${basePath}/digi-tube/home`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && navigate(`${basePath}/digi-tube/home`)}
                  style={{ cursor: 'pointer', fontStyle: 'normal' }}
                >DIGI TUBE</span>
              </>
            )}
            {isOdin && (
              <>
                <span className={styles.logoDivider}>/</span>
                <span
                  className={styles.logoTextEcpt}
                  onClick={() => navigate(`${basePath}/odin/home`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && navigate(`${basePath}/odin/home`)}
                  style={{ cursor: 'pointer', fontStyle: 'normal' }}
                >ODIN</span>
              </>
            )}
          </div>

          {/* Mercedes-Benz center logo */}
          <div className={styles.headerCenter}>
            <img
              src={mercedesBenzLogo}
              alt="Mercedes-Benz"
              className={styles.mercedesLogo}
            />
          </div>

          {/* Controls */}
          <div className={styles.headerControls}>
            {/* User avatar + dropdown */}
            <div
              ref={profileRef}
              className={styles.userAvatar}
              onClick={() => setProfileOpen((open) => !open)}
            >
              <div
                className={styles.avatarIconShell}
                role="button"
                aria-label="User profile"
                aria-expanded={profileOpen}
              >
                <UserIcon />
              </div>

              {profileOpen && (
                <div className={styles.profileDropdown} role="menu" aria-label="User menu">
                  <p className={styles.profileDropdownName}>{userName}</p>
                  <p className={styles.profileDropdownRole}>{t('homePage.user.role')}</p>
                  <hr className={styles.profileDivider} />
                  <button
                    type="button"
                    className={styles.profileSettingsBtn}
                    onClick={openProfileSettings}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
                      <circle cx="12" cy="12" r="3" />
                      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0 .33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68 1.65 1.65 0 0 0 10 3.17V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                    </svg>
                    Profile Settings
                  </button>
                  <button
                    type="button"
                    className={styles.profileLogoutBtn}
                    onClick={handleLogout}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                      strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                      <polyline points="16 17 21 12 16 7" />
                      <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                    Log out
                  </button>
                </div>
              )}
            </div>

            {/* ECPT Settings shortcut */}
            {isEcpt && (
              <button
                type="button"
                className={styles.headerControlButton}
                onClick={() => {
                  const from = pathname.includes('/ecpt/get') ? 'get'
                    : pathname.includes('/ecpt/operations') ? 'operations'
                    : pathname.includes('/ecpt/home') ? 'home'
                    : undefined;
                  navigate(`${basePath}/ecpt/settings`, { state: { from } });
                }}
                aria-label={t('ecpt.nav.settings')}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
              </button>
            )}

            {/* Theme toggle */}
            <button
              type="button"
              className={styles.headerControlButton}
              onClick={toggleTheme}
              aria-label={isDark ? t('homePage.header.themeLight') : t('homePage.header.themeDark')}
            >
              {isDark ? <SunIcon /> : <MoonIcon />}
              <span>{isDark ? t('homePage.header.themeLight') : t('homePage.header.themeDark')}</span>
            </button>

            {/* Language toggle */}
            <button
              type="button"
              className={styles.headerControlButton}
              onClick={toggleLanguageHandler}
              aria-label="Switch language"
            >
              <GlobeIcon />
              <span>{currentLangLabel}</span>
            </button>

          </div>
        </div>
      </div>
    </header>
  );
};

export default AppBar;
